/**
 * Projects Tour Mock Data
 * 
 * Mock data specifically for the "Welcome to Projects" tour (or future project-related tours).
 * This data is curated to provide a consistent, educational experience during tours.
 */

import type { Project, Client, Employee } from '@/types';

/**
 * Mock clients for tour mode
 */
export const tourMockClients: Client[] = [
  {
    id: 1001,
    name: 'Acme Corporation',
    industry: 'Technology',
    contactName: 'John Smith',
    contactEmail: 'john.smith@acme.com',
  },
  {
    id: 1002,
    name: 'Global Industries',
    industry: 'Manufacturing',
    contactName: 'Sarah Johnson',
    contactEmail: 'sarah.j@globalind.com',
  },
];

/**
 * Mock employees for tour mode
 */
export const tourMockEmployees: Employee[] = [
  {
    id: 2001,
    name: 'Alice Manager',
    email: 'alice@company.com',
    role: 'Project Manager',
    department: 'Delivery',
    costRate: 75,
    billRate: 150,
    skills: ['Project Management', 'Agile', 'Stakeholder Management'],
    isAvailable: true,
  },
  {
    id: 2002,
    name: 'Bob Developer',
    email: 'bob@company.com',
    role: 'Senior Developer',
    department: 'Engineering',
    costRate: 65,
    billRate: 130,
    skills: ['React', 'TypeScript', 'Node.js'],
    isAvailable: true,
  },
];

/**
 * Mock projects for tour mode
 */
export const tourMockProjects: Project[] = [
  {
    id: 3001,
    name: 'Website Redesign',
    clientIds: '1001',
    clientNames: 'Acme Corporation',
    projectManagerId: 2001,
    type: 'fixed-price',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-01-15',
    endDate: '2024-06-30',
    description: 'Complete redesign of corporate website with modern UI/UX',
    
    // Financial
    budgetLabour: 120000,
    budgetExpense: 15000,
    budgetRevenue: 180000,
    actualLabour: 45000,
    actualExpense: 5000,
    actualRevenue: 60000,
    
    // Hours/Resource Budget
    budgetHours: 1600,
    actualHours: 600,
    hoursVariance: 1000,
    hoursVariancePercent: 62.5,
    
    // Calculated
    totalBudget: 135000,
    totalActual: 50000,
    variance: 85000,
    variancePercent: 63,
    marginPercent: 25,
    
    // Progress
    completionPercent: 35,
    
    // Revenue Recognition
    revenueRecognitionMethod: 'percentage-of-completion',
    
    // WIP
    wipAmount: 15000,
    unbilledAmount: 10000,
    
    // Metadata
    createdAt: '2024-01-10T10:00:00Z',
    updatedAt: '2024-03-15T14:30:00Z',
  },
  {
    id: 3002,
    name: 'Mobile App Development',
    clientIds: '1002',
    clientNames: 'Global Industries',
    projectManagerId: 2001,
    type: 'time-and-materials',
    status: 'in-progress',
    health: 'amber',
    startDate: '2024-02-01',
    endDate: '2024-08-31',
    description: 'Native mobile application for iOS and Android platforms',
    
    // Financial
    budgetLabour: 200000,
    budgetExpense: 25000,
    budgetRevenue: 300000,
    actualLabour: 85000,
    actualExpense: 12000,
    actualRevenue: 120000,
    
    // Hours/Resource Budget
    budgetHours: 2667,
    actualHours: 1133,
    hoursVariance: 1534,
    hoursVariancePercent: 57.5,
    
    // Calculated
    totalBudget: 225000,
    totalActual: 97000,
    variance: 128000,
    variancePercent: 56.9,
    marginPercent: 28,
    
    // Progress
    completionPercent: 42,
    
    // Revenue Recognition
    revenueRecognitionMethod: 'time-based',
    
    // WIP
    wipAmount: 22000,
    unbilledAmount: 15000,
    
    // Metadata
    createdAt: '2024-01-25T09:00:00Z',
    updatedAt: '2024-03-16T11:20:00Z',
  },
];
